import dayjs, { type Dayjs } from 'dayjs'
import {
  Condition,
  ConditionType,
  CoolingOffType,
  DepositType,
  ValueType,
  type CoolingOffDetails,
  type Deposit,
} from '@urbanx/agx.campaigns.client'

type DepositAmountFields = {
  amount?: number | null
  percent?: number | null
}

const roundCurrencyAmount = (value?: number | null) =>
  Math.round((value ?? 0) * 100) / 100

export const getAmountFromDeposit = (
  deposit?: DepositAmountFields,
  salePrice?: number
) => {
  if (deposit?.amount != null) return roundCurrencyAmount(deposit.amount)
  if (deposit?.percent != null && salePrice != null) {
    return roundCurrencyAmount(salePrice * (deposit.percent / 100))
  }

  return 0
}

export const getTotalDepositAmount = ({
  initialDeposit,
  balanceDeposit,
  salePrice,
}: {
  initialDeposit?: DepositAmountFields
  balanceDeposit?: DepositAmountFields
  salePrice?: number
}) =>
  getAmountFromDeposit(initialDeposit, salePrice) +
  getAmountFromDeposit(balanceDeposit, salePrice)

export const getDepositExcessAmount = ({
  totalDepositAmount,
  salePrice,
}: {
  totalDepositAmount: number
  salePrice?: number | null
}) => {
  if (salePrice == null) return undefined

  const tenPercentOfSalePrice = salePrice / 10
  return roundCurrencyAmount(totalDepositAmount - tenPercentOfSalePrice)
}

export const getNswDepositSplit = ({
  totalDepositAmount,
  salePrice,
  fullDepositUpfront,
}: {
  totalDepositAmount: number
  salePrice: number
  fullDepositUpfront: boolean
}) => {
  if (fullDepositUpfront) {
    return {
      initialDepositAmount: totalDepositAmount,
      balanceDepositAmount: undefined,
    }
  }

  // NSW split deposits collect 0.25% upfront and defer the remaining balance.
  const initialDepositAmount = getAmountFromDeposit(
    { percent: 0.25 },
    salePrice
  )
  const balanceDepositAmount = roundCurrencyAmount(
    totalDepositAmount - initialDepositAmount
  )

  return {
    initialDepositAmount,
    balanceDepositAmount:
      balanceDepositAmount > 0 ? balanceDepositAmount : undefined,
  }
}

const getCoolingOffBusinessDays = (
  coolingOffDetails?: Pick<
    CoolingOffDetails,
    'coolingOffType' | 'coolingOffDays'
  > | null
) => {
  switch (coolingOffDetails?.coolingOffType) {
    case CoolingOffType.FiveDays:
      return 5
    case CoolingOffType.TenDays:
      return 10
    case CoolingOffType.NDays:
      return typeof coolingOffDetails.coolingOffDays === 'number' &&
        coolingOffDetails.coolingOffDays > 0
        ? coolingOffDetails.coolingOffDays
        : undefined
    case CoolingOffType.NoCoolingOff:
    default:
      return undefined
  }
}

export const getNswBalanceDepositDueDate = ({
  contractDate,
  coolingOffDetails,
  addBusinessDays,
  moveToNextBusinessDay,
}: {
  contractDate?: Date | null
  coolingOffDetails?: Pick<
    CoolingOffDetails,
    'coolingOffType' | 'coolingOffDays'
  > | null
  addBusinessDays: (startDate: Dayjs, days: number) => Dayjs
  moveToNextBusinessDay: (date: Dayjs) => Dayjs
}) => {
  if (!contractDate) return undefined

  const contractDateValue = dayjs(contractDate)
  const coolingOffBusinessDays = getCoolingOffBusinessDays(coolingOffDetails)
  const dueDate =
    coolingOffBusinessDays === undefined
      ? contractDateValue
      : addBusinessDays(contractDateValue, coolingOffBusinessDays)

  return moveToNextBusinessDay(dueDate).toDate()
}

export const getNswBalanceDepositWithDueDate = ({
  deposit,
  dueDateString,
}: {
  deposit?: Deposit
  dueDateString?: string
}) => {
  if (
    !deposit ||
    deposit.depositType !== DepositType.Balance ||
    !dueDateString
  ) {
    return deposit
  }

  return {
    ...deposit,
    condition: Condition.OnDate,
    conditionType: ConditionType.Unconditional,
    referenceDate: dueDateString,
  }
}

export const normalizeDepositToDollar = (
  deposit: Deposit | undefined,
  salePrice?: number
): Deposit | undefined => {
  if (!deposit) return undefined

  if (
    deposit.amount != null &&
    deposit.depositValueType === ValueType.Dollar &&
    deposit.percent == null
  ) {
    return deposit
  }

  const amount =
    deposit.amount != null
      ? deposit.amount
      : deposit.percent != null && salePrice != null
        ? getAmountFromDeposit(deposit, salePrice)
        : undefined

  // Keep percentage deposits intact until sale price is available to derive dollars.
  if (amount == null) return deposit

  return {
    ...deposit,
    amount,
    percent: undefined,
    depositValueType: ValueType.Dollar,
  }
}

export const getDepositWithEditedCondition = ({
  deposit,
  updatedDeposit,
  isNsw,
  salePrice,
  amount,
  percent,
}: {
  deposit: Deposit
  updatedDeposit: Deposit
  isNsw: boolean
  salePrice?: number
  amount?: number
  percent?: number
}): Deposit => {
  const nextDeposit = {
    ...deposit,
    ...updatedDeposit,
    depositType: deposit.depositType,
  }

  if (isNsw) {
    return normalizeDepositToDollar(nextDeposit, salePrice) ?? nextDeposit
  }

  return {
    ...nextDeposit,
    amount: amount ?? nextDeposit.amount,
    percent: percent ?? nextDeposit.percent,
    depositValueType: deposit.depositValueType,
  }
}
